import React from "react";

/**
 * Renders AI chat message content with simple markdown-style formatting:
 * # H1, ## H2, ### H3, **bold**, - bullet lists, preserved line breaks.
 * Safe for use in both user (dark) and assistant (light) bubbles via className.
 */
const ChatMessageContent = ({ content, className = "text-sm break-words" }) => {
  if (content == null || content === "") return null;
  const text = typeof content === "string" ? content : String(content);

  const renderInline = (str) => {
    const parts = [];
    let remaining = str;
    let key = 0;
    while (remaining.length > 0) {
      const boldStart = remaining.indexOf("**");
      if (boldStart === -1) {
        parts.push(<span key={key++}>{remaining}</span>);
        break;
      }
      if (boldStart > 0) {
        parts.push(<span key={key++}>{remaining.slice(0, boldStart)}</span>);
      }
      remaining = remaining.slice(boldStart + 2);
      const boldEnd = remaining.indexOf("**");
      if (boldEnd === -1) {
        parts.push(<span key={key++}>**{remaining}</span>);
        break;
      }
      parts.push(<strong key={key++}>{remaining.slice(0, boldEnd)}</strong>);
      remaining = remaining.slice(boldEnd + 2);
    }
    return parts;
  };

  const lines = text.split(/\r?\n/);
  const elements = [];
  let listItems = [];
  let key = 0;

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={key++} className="list-disc list-inside my-2 space-y-0.5 pl-1">
          {listItems.map((item, i) => (
            <li key={i}>{renderInline(item)}</li>
          ))}
        </ul>
      );
      listItems = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed.startsWith("### ")) {
      flushList();
      elements.push(
        <h3 key={key++} className="text-sm font-semibold mt-2 mb-0.5 first:mt-0">
          {renderInline(trimmed.slice(4))}
        </h3>
      );
      continue;
    }
    if (trimmed.startsWith("## ")) {
      flushList();
      elements.push(
        <h2 key={key++} className="text-base font-semibold mt-3 mb-1 first:mt-0">
          {renderInline(trimmed.slice(3))}
        </h2>
      );
      continue;
    }
    if (trimmed.startsWith("# ")) {
      flushList();
      elements.push(
        <h1 key={key++} className="text-lg font-bold mt-3 mb-1 first:mt-0">
          {renderInline(trimmed.slice(2))}
        </h1>
      );
      continue;
    }
    if (/^-\s+/.test(trimmed) || /^\*\s+/.test(trimmed)) {
      const bullet = trimmed.replace(/^[-*]\s+/, "");
      listItems.push(bullet);
      continue;
    }

    flushList();
    if (trimmed === "") {
      elements.push(<div key={key++} className="h-2" />);
      continue;
    }
    elements.push(
      <p key={key++} className="whitespace-pre-wrap my-1 first:mt-0 last:mb-0">
        {renderInline(line)}
      </p>
    );
  }
  flushList();

  return <div className={className}>{elements}</div>;
};

export default ChatMessageContent;
